import { assert, setDifference, setIntersection } from '@sama/js-core';
import { clamp, range } from '@sama/micro-dash';
import { SubscriptionManager } from '@sama/rxjs-core';
import { BufferGeometry, Line, LineBasicMaterial, Vector3 } from 'three';
import { isKeyLocation } from '../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { CuboidLocationState3D } from '../../../state/cuboid-location-state-3d';
import { interpolateLocation } from '../../../utils/point-cloud-interpolation-helper';
import { RendererLayer, setRendererLayers } from '../../render-utils';
import { getRouteMeshName, RouteMarker } from './route-marker';

export const ROUTE_RENDERER_LAYERS = [RendererLayer.Main];
export const connectorMaterial = new LineBasicMaterial({ color: '#56B8AF' });

/**
 * Represents the maximum number of possible frames
 * to be displayed ahead and behind the current frame
 */
const routeLimit = 15;

export class CuboidRouteHelper extends SubscriptionManager {
  private markers = new Map<number, RouteMarker>();
  private connectors = new Map<number, Line>();
  private connectorPositionVectors = [new Vector3(), new Vector3()];

  constructor(
    private frameCount: number,
    private currentFrameNumber: number,
    private cuboidIndex: number,
    locations: CuboidLocationState3D[],
  ) {
    super();
    this.updateLocations(locations, currentFrameNumber);
  }

  getMeshes() {
    return [...this.markers.values(), ...this.connectors.values()];
  }

  cleanUp() {
    this.connectors.forEach((object) => {
      object.geometry.dispose();
    });

    this.markers.clear();
    this.connectors.clear();
    this.unsubscribe();
  }

  updateLocations(
    updatedLocations: CuboidLocationState3D[],
    currentFrameNumber: number,
  ) {
    const existingMarkers = new Set(this.markers.keys());
    const updatedMarkers = new Set(
      updatedLocations.map((location) => location.frame_number),
    );

    // Current frame should always have a marker
    updatedMarkers.add(currentFrameNumber);

    const frameNumbersToCreate = setDifference(updatedMarkers, existingMarkers);
    const frameNumbersToUpdate = setIntersection(
      existingMarkers,
      updatedMarkers,
    );
    const frameNumbersToRemove = setDifference(existingMarkers, updatedMarkers);

    this.currentFrameNumber = currentFrameNumber;
    this.createMarkers(Array.from(frameNumbersToCreate), updatedLocations);
    this.updateMarkers(Array.from(frameNumbersToUpdate), updatedLocations);
    this.removeMarkers(Array.from(frameNumbersToRemove));

    // Destroy and rebuild connectors every time for now
    this.connectors.clear();
    this.createConnectors(updatedLocations);
  }

  private createMarkers(
    frameNumbers: number[],
    updatedLocations: CuboidLocationState3D[],
  ) {
    this.getFrameNumbersWithinBounds(frameNumbers).forEach((frameNumber) => {
      const marker = new RouteMarker(frameNumber, this.cuboidIndex);
      this.updateMarker(marker, updatedLocations, frameNumber);
      this.markers.set(frameNumber, marker);
    });
  }

  private updateMarkers(
    frameNumbers: number[],
    updatedLocations: CuboidLocationState3D[],
  ) {
    this.getFrameNumbersWithinBounds(frameNumbers).forEach((frameNumber) => {
      const marker = this.markers.get(frameNumber);
      assert(marker, 'Each key location + current frame should have a marker');
      this.updateMarker(marker, updatedLocations, frameNumber);
    });
  }

  private updateMarker(
    marker: RouteMarker,
    locations: CuboidLocationState3D[],
    frameNumber: number,
  ) {
    marker.updateLocation(
      // Interpolation is used for the current frame number, otherwise markers
      // are only on keyframes, which interpolateLocation() returns instantly
      interpolateLocation(locations, frameNumber),
      this.currentFrameNumber,
      !!locations.find((l) => l.frame_number === frameNumber),
      interpolateLocation(locations, frameNumber - 1).visibility === 1,
    );
  }

  private removeMarkers(frameNumbers: number[]) {
    frameNumbers.forEach((frameNumber) => {
      this.markers.delete(frameNumber);
    });
  }

  private createConnectors(keyLocations: CuboidLocationState3D[]) {
    const [lowerBound, upperBound] = this.getFrameNumberBounds();
    let lateKeyLocationFrameNumber = upperBound;

    // The sequence is traversed from right to left (future to past),
    // in order to keep in memory the future visibily state
    for (const frameNumber of range(upperBound, lowerBound - 1, -1)) {
      if (isKeyLocation(keyLocations, frameNumber)) {
        this.createOrUpdateConnector(
          keyLocations,
          frameNumber,
          lateKeyLocationFrameNumber,
        );

        lateKeyLocationFrameNumber = frameNumber;
      } else if (frameNumber === this.currentFrameNumber) {
        // Create extra connector and marker for current frame transformations
        this.createOrUpdateConnector(
          keyLocations,
          frameNumber,
          lateKeyLocationFrameNumber,
        );
      }
    }
  }

  private createOrUpdateConnector(
    keyLocations: CuboidLocationState3D[],
    frameNumber: number,
    lastKeyLocationFrameNumber: number,
  ) {
    let connector = this.connectors.get(frameNumber);
    if (!connector) {
      connector = this.createConnector(frameNumber);
      this.connectors.set(frameNumber, connector);
    }

    // The position order is in reverse, so it's easier to reason about
    // when testing and debugging
    // We contruct the connector position such that the starting point is
    // the earlier frame in the sequence. This way we can think of the
    // route always going from frame 0 to frame N
    const laterLocation = interpolateLocation(
      keyLocations,
      lastKeyLocationFrameNumber,
    );
    const location = interpolateLocation(keyLocations, frameNumber);
    connector.geometry.setFromPoints([
      this.connectorPositionVectors[0].fromArray(location.position),
      this.connectorPositionVectors[1].fromArray(laterLocation.position),
    ]);
    connector.geometry.computeBoundingSphere();
  }

  private createConnector(frameNumber: number) {
    const connector = new Line(new BufferGeometry(), connectorMaterial);
    connector.name = getRouteMeshName(this.cuboidIndex, frameNumber);
    connector.userData = { meshType: 'RouteConnector' };

    setRendererLayers(ROUTE_RENDERER_LAYERS, connector);

    return connector;
  }

  private getFrameNumbersWithinBounds(frameNumbers: number[]) {
    const frameRange = this.getFrameNumberBounds();
    return frameNumbers.filter(
      (frameNumber) =>
        frameNumber >= frameRange[0] && frameNumber <= frameRange[1],
    );
  }

  private getFrameNumberBounds() {
    const upperBound = this.currentFrameNumber + routeLimit;
    const lowerBound = this.currentFrameNumber - routeLimit;

    return [lowerBound, upperBound].map((bound) =>
      clamp(bound, 0, this.frameCount - 1),
    ) as Point2D;
  }
}
